// Form factory + validação da etapa Configurações e Rotas (TMS-6397).
// Shape alinhado ao AS-IS do PUT /tabelafrete/store + itinerary_id (ADR-0010).

import {
  FREIGHT_TABLE_TIPO_RECEITA,
  TP_NAO_DEFINIDO,
  calcIsFaixaPesoPorVeiculo,
  calcRequiresRanges,
  calcUsesItinerary,
  calcUsesVehicle,
} from "../constants/freightTableOptions";
import {
  TAXA_DEFAULTS,
  createEmptyCustoExtra,
  isCustoExtraComplete,
} from "../utils/validityTaxes";

export { createEmptyCustoExtra, isCustoExtraComplete };

export function createEmptyFaixa() {
  return {
    vlr_ini: null,
    vlr_fim: null,
    vlr: null,
    vlr_exd: null,
    fixa: 0,
    excedente: 0,
    fator_conv: null,
    status: 1,
  };
}

/** Faixa com qualquer coisa digitada — no frame a lixeira só aparece nessas. */
export function isFaixaPreenchida(faixa) {
  if (!faixa) return false;

  const campos = ["vlr_ini", "vlr_fim", "vlr", "fator_conv", "vlr_exd"];
  const temValor = campos.some(
    (campo) => faixa[campo] !== null && faixa[campo] !== "",
  );

  return temValor || Number(faixa.fixa) === 1 || Number(faixa.excedente) === 1;
}

/**
 * Ids de veículo sempre como lista, sem repetição. Aceita escalar porque os
 * campos de veículo já foram selects simples e um rascunho em tela pode estar
 * no formato antigo.
 */
export function normalizeVehicleIds(raw) {
  const list = Array.isArray(raw) ? raw : [raw];

  return list.reduce((ids, id) => {
    if (id === null || id === undefined || id === "") return ids;
    const numericId = Number(id);
    const normalized = Number.isFinite(numericId) ? numericId : id;
    return ids.indexOf(normalized) === -1 ? ids.concat([normalized]) : ids;
  }, []);
}

/** Veículos de uma linha de valor de saída — uma linha atende 1..N veículos. */
export function valorSaidaLineVehicles(linha) {
  return linha ? normalizeVehicleIds(linha.tp_rodado) : [];
}

export function createEmptyValorSaidaLine() {
  return {
    vlr: null,
    tp_rodado: [],
  };
}

/** Linha preenchida: valor e ao menos um veículo informados. */
export function isValorSaidaLineComplete(linha) {
  return (
    linha != null &&
    linha.vlr !== null &&
    linha.vlr !== "" &&
    valorSaidaLineVehicles(linha).length > 0
  );
}

/** Valor sem veículo — bloqueia o salvamento (TMS-7167 / CT03). */
export function validateValoresSaida(vigencia) {
  if (!vigencia || !vigencia.saidaVeiculoAtiva) return [];

  const jaUsados = [];

  return (vigencia.valoresSaida || []).reduce((errors, linha, index) => {
    const hasVlr = linha && linha.vlr !== null && linha.vlr !== "";
    const veiculos = valorSaidaLineVehicles(linha);

    if (hasVlr && !veiculos.length) {
      errors.push({
        index,
        message:
          "Tipo de veículo é obrigatório quando o valor de saída for informado.",
      });
    }

    // O payload tem um item de transp por veículo: repetir o veículo em outra
    // linha faria o último valor sobrescrever o anterior sem aviso na tela.
    const repetidos = veiculos.filter((id) => jaUsados.indexOf(id) !== -1);
    veiculos.forEach((id) => {
      if (jaUsados.indexOf(id) === -1) jaUsados.push(id);
    });

    if (repetidos.length) {
      errors.push({
        index,
        message:
          "Cada veículo só pode ter um valor de saída na vigência: remova o veículo repetido em outra linha.",
      });
    }

    return errors;
  }, []);
}

/**
 * `transp` pertence à rota, não à vigência. Repetir um veículo em vigências
 * diferentes faria o último valor de saída sobrescrever o anterior no mapper.
 */
export function validateValoresSaidaDuplicadosNaRota(route) {
  const vigenciaPorVeiculo = new Map();

  return ((route && route.vigencia) || []).reduce(
    (errors, vigencia, vigIndex) => {
      if (!vigencia || !vigencia.saidaVeiculoAtiva) return errors;

      (vigencia.valoresSaida || []).forEach((linha, lineIndex) => {
        valorSaidaLineVehicles(linha).forEach((vehicleId) => {
          if (
            vigenciaPorVeiculo.has(vehicleId) &&
            vigenciaPorVeiculo.get(vehicleId) !== vigIndex
          ) {
            errors.push({
              vigIndex,
              lineIndex,
              message:
                "Cada veículo só pode ter um valor de saída na rota: remova o veículo repetido em outra vigência.",
            });
            return;
          }

          vigenciaPorVeiculo.set(vehicleId, vigIndex);
        });
      });

      return errors;
    },
    [],
  );
}

export function isValorVariavelInformado(faixa) {
  if (!faixa) return false;
  if (faixa.vlr === null || faixa.vlr === undefined || faixa.vlr === "") {
    return false;
  }
  return Number(faixa.vlr) > 0;
}

export function validateValorVariavelVeiculo(vigencia, tpCalc) {
  if (!vigencia || !calcIsFaixaPesoPorVeiculo(tpCalc)) return [];

  const temVeiculo = validityVehicles(vigencia).length > 0;
  if (temVeiculo) return [];

  return (vigencia.calculo || []).reduce((errors, faixa, index) => {
    if (isValorVariavelInformado(faixa)) {
      errors.push({
        index,
        message:
          "Tipo de veículo é obrigatório quando o valor variável for informado.",
      });
    }

    return errors;
  }, []);
}

function isBlank(value) {
  return value === null || value === undefined || value === "";
}

/** Faixa preenchida: início, fim e valor. vlr_fim = 0 é recusado pelo §4.8. */
export function isFaixaComplete(faixa) {
  return (
    faixa != null &&
    !isBlank(faixa.vlr_ini) &&
    !isBlank(faixa.vlr_fim) &&
    Number(faixa.vlr_fim) !== 0 &&
    !isBlank(faixa.vlr)
  );
}

/** Veículos gerais da vigência. */
export function validityVehicles(validity) {
  return validity ? normalizeVehicleIds(validity.tp_rodado) : [];
}

/** "DD/MM/YYYY" → "YYYYMMDD", comparável como string. Fora do formato, null. */
function validityDateKey(value) {
  const parts = String(value || "").split("/");
  return parts.length === 3 ? `${parts[2]}${parts[1]}${parts[0]}` : null;
}

/** Linha de vigência sem nada preenchido — o formulário nasce com uma. */
export function isValidityBlank(validity) {
  if (!validity) return true;

  return (
    !validity.dt_vigencia_ini &&
    !validity.dt_vigencia_fim &&
    isBlank(validity.vlr) &&
    !validityVehicles(validity).length &&
    !validity.saidaVeiculoAtiva &&
    !(validity.valoresSaida || []).some(
      (linha) =>
        linha &&
        (!isBlank(linha.vlr) || valorSaidaLineVehicles(linha).length > 0),
    ) &&
    !(validity.calculo || []).some(
      (faixa) =>
        faixa &&
        (!isBlank(faixa.vlr_ini) ||
          !isBlank(faixa.vlr_fim) ||
          !isBlank(faixa.vlr)),
    )
  );
}

/**
 * Períodos das vigências da rota (TMS-8016): início sempre obrigatório; fim
 * opcional. Duas abertas são ok na tela — o back fecha a anterior em D-1
 * (applyVigenciaEndDates). Aqui só bloqueia início duplicado ou intervalo
 * fechado que invade o início da seguinte.
 */
export function validateValidityPeriods(route) {
  const validities = (route && route.vigencia) || [];
  const erros = [];

  validities.forEach((validity, index) => {
    const ini = validityDateKey(validity.dt_vigencia_ini);
    const fim = validityDateKey(validity.dt_vigencia_fim);

    if (!isValidityBlank(validity) && !ini) {
      erros.push(`Vigência ${index + 1}: informe a data inicial`);
    }
    if (ini && fim && fim < ini) {
      erros.push(`Vigência ${index + 1}: data final anterior à inicial`);
    }
  });

  // O back encadeia as vigências em ordem cronológica e fecha a que está aberta
  // em D-1 do início da seguinte (applyVigenciaEndDates), então sobra conflito
  // só quando duas começam no mesmo dia ou quando um período fechado invade o
  // início do próximo.
  const periodos = validities
    .map((validity, index) => ({
      numero: index + 1,
      ini: validityDateKey(validity.dt_vigencia_ini),
      fim: validityDateKey(validity.dt_vigencia_fim),
    }))
    .filter((periodo) => periodo.ini !== null)
    .sort((a, b) => a.ini.localeCompare(b.ini));

  periodos.forEach((atual, index) => {
    const proxima = periodos[index + 1];
    if (!proxima) return;

    if (proxima.ini <= atual.ini || (atual.fim && proxima.ini <= atual.fim)) {
      erros.push(
        `Vigências ${atual.numero} e ${proxima.numero} têm períodos sobrepostos`,
      );
    }
  });

  return erros;
}

export function createEmptyVigencia() {
  return {
    dt_vigencia_ini: null,
    dt_vigencia_fim: null,
    vlr: null,
    tp_rodado: [],
    saidaVeiculoAtiva: false,
    valoresSaida: [createEmptyValorSaidaLine()],
    ...TAXA_DEFAULTS,
    custo_extra: [],
    calculo: [createEmptyFaixa()],
  };
}

// Sequência de ids de lista. Só existe no cliente: dá uma :key estável para as
// rotas, que sobrevive a remoções no meio da lista (o índice não sobreviveria).
let routeUidSeq = 0;

export function createEmptyRoute(numRota = 1) {
  return {
    // --- client-only (o mapper do PUT deve descartar) ---
    // uid: chave de lista; open/transporteOpen/showDescricao: colapsos;
    // routeTab: aba ativa. Estado de UI por rota, não vai para a API.
    uid: ++routeUidSeq,
    open: true,
    transporteOpen: true,
    routeTab: "trajeto",
    showDescricao: false,
    // --- payload ---
    num_rota: numRota,
    descricao: "",
    status: 1,
    // tp_origem/tp_destino são derivados no mapper a partir de qual lado do par
    // município/região foi preenchido (§4.8).
    municipio_origem: null,
    regiao_origem: null,
    municipio_destino: null,
    regiao_destino: null,
    itinerary_id: null,
    tp_processo: TP_NAO_DEFINIDO,
    tp_operacao: null,
    carga: [{ param_tipo_carga: null, tp_carga: null, status: 1 }],
    // transp[] não fica no estado: o mapper deriva a partir dos veículos das
    // vigências (ADR-0010 §5).
    vigencia: [createEmptyVigencia()],
  };
}

export function createEmptyConfigRoutesForm() {
  return {
    tp_calc: null,
    tp_tabela: 2, // Fracionada — 1 é Lotação; o Figma marca Fracionada (ADR-0013 §4)
    tp_receita_despesa: null,
    tp_composicao: 1,
    // Cliente/tomador: só vai no payload quando o tipo é receita, que é quando o backend exige.
    pessoa: [],
    rota: [createEmptyRoute(1)],
  };
}

/**
 * No tipo 8 o veículo sai da completude genérica e passa a ser cobrado por
 * `validateValorVariavelVeiculo`, com a mensagem exata do critério de aceite —
 * senão a mesma falta apareceria duas vezes, com dois textos diferentes.
 */
function hasCompleteValidity(route, tpCalc) {
  const porFaixa = calcRequiresRanges(tpCalc);
  const comVeiculo =
    calcUsesVehicle(tpCalc) && !calcIsFaixaPesoPorVeiculo(tpCalc);

  return (route.vigencia || []).some((v) => {
    if (!v.dt_vigencia_ini) return false;
    if (comVeiculo && !validityVehicles(v).length) return false;
    if (porFaixa) return (v.calculo || []).some(isFaixaComplete);
    return v.vlr !== null && v.vlr !== "";
  });
}

/**
 * Pendências da rota agrupadas por onde o campo aparece na tela. Dois dos
 * obrigatórios moram dentro da aba Carga, que não é a aba aberta por default —
 * sem isso o operador não tem como saber o que falta. Características não tem
 * mais obrigatório (ADR-0013 §3), mas a chave continua: o template lê as três.
 */
function describeValidityGap(tpCalc) {
  const partes = ["data inicial"];
  if (calcUsesVehicle(tpCalc) && !calcIsFaixaPesoPorVeiculo(tpCalc)) {
    partes.push("veículo");
  }
  partes.push(calcRequiresRanges(tpCalc) ? "uma faixa completa" : "valor");

  return `Vigência com ${partes.join(", ")}`;
}

export function routeGaps(route, tpCalc) {
  const gaps = { trajeto: [], carga: [], caracteristicas: [], transporte: [] };
  if (!route) return gaps;

  const hasCompleteCarga = (route.carga || []).some(
    (carga) =>
      carga &&
      carga.param_tipo_carga != null &&
      carga.tp_carga != null &&
      carga.status != null,
  );

  // Viagem define a rota por roteiro, com origem só por município e sem destino;
  // nos outros tipos o trajeto é origem→destino, cada lado por município ou
  // região (§4.8.7).
  if (calcUsesItinerary(tpCalc)) {
    if (!route.municipio_origem) gaps.trajeto.push("Município origem");
    if (!route.itinerary_id) gaps.trajeto.push("Roteiro");
  } else {
    if (!route.municipio_origem && !route.regiao_origem) {
      gaps.trajeto.push("Município ou região de origem");
    }
    if (!route.municipio_destino && !route.regiao_destino) {
      gaps.trajeto.push("Município ou região de destino");
    }
  }
  if (!hasCompleteCarga) {
    gaps.carga.push("Tipo carga");
    gaps.carga.push("Tipo");
  }
  if (route.tp_operacao == null) gaps.caracteristicas.push("Tipo de operação");
  if (route.tp_processo == null) gaps.caracteristicas.push("Tipo processo");
  if (!hasCompleteValidity(route, tpCalc)) {
    gaps.transporte.push(describeValidityGap(tpCalc));
  }

  (route.vigencia || []).forEach((vigencia, vigIndex) => {
    validateValoresSaida(vigencia).forEach((erro) => {
      gaps.transporte.push(
        `Valor de saída (vigência ${vigIndex + 1}, linha ${erro.index + 1}): ${erro.message}`,
      );
    });

    validateValorVariavelVeiculo(vigencia, tpCalc).forEach((erro) => {
      gaps.transporte.push(
        `Valor variável (vigência ${vigIndex + 1}, faixa ${erro.index + 1}): ${erro.message}`,
      );
    });
  });

  validateValoresSaidaDuplicadosNaRota(route).forEach((erro) => {
    gaps.transporte.push(
      `Valor de saída (vigência ${erro.vigIndex + 1}, linha ${erro.lineIndex + 1}): ${erro.message}`,
    );
  });

  validateValidityPeriods(route).forEach((erro) => gaps.transporte.push(erro));

  return gaps;
}

function routeGapList(route, tpCalc) {
  const gaps = routeGaps(route, tpCalc);
  return gaps.trajeto
    .concat(gaps.carga, gaps.caracteristicas, gaps.transporte)
    .filter(Boolean);
}

/**
 * Rota completa: origem, roteiro, carga, características e ao menos uma vigência
 * com data início, valor e veículo. Também alimenta a bolinha do header
 * (verde = completa). Deriva de routeGaps para não divergir do que o tooltip
 * do botão lista.
 */
export function isRouteValid(route, tpCalc) {
  return !!route && routeGapList(route, tpCalc).length === 0;
}

/**
 * Pendências da etapa 3 em texto, uma linha por rota — alimenta o tooltip do
 * botão desabilitado. Vazio significa "pode salvar".
 */
export function describeConfigRoutesGaps(form) {
  if (!form) return ["Configurações e Rotas não preenchidas"];

  const header = [];
  if (form.tp_calc == null) header.push("Tipo cálculo");
  if (form.tp_tabela == null) header.push("Tabela");
  if (form.tp_receita_despesa == null) header.push("Tipo");
  if (form.tp_composicao == null) header.push("Composição");
  if (isReceita(form) && !(form.pessoa || []).length) header.push("Clientes");

  if (!form.rota || !form.rota.length) {
    return header
      .map((campo) => `Configurações: ${campo}`)
      .concat("Ao menos uma rota");
  }

  const rotas = form.rota
    .map((rota, index) => {
      const gaps = routeGapList(rota, form.tp_calc);
      if (!gaps.length) return null;
      return `Rota ${String(index + 1).padStart(2, "0")}: ${gaps.join(", ")}`;
    })
    .filter(Boolean);

  return (header.length ? [`Configurações: ${header.join(", ")}`] : []).concat(
    rotas,
  );
}

// Tabela receita sem cliente é recusada pelo backend (`pessoa` é obrigatória quando o tipo é
// receita), então o botão Criar tabela trava antes de o operador perder o preenchimento.
function isReceita(form) {
  return Number(form.tp_receita_despesa) === FREIGHT_TABLE_TIPO_RECEITA;
}

function isTpNaoDefinido(value) {
  return (
    value === null || value === undefined || Number(value) === TP_NAO_DEFINIDO
  );
}

/** Rota "em branco" — permite remover sem confirmação. */
export function isRouteEmpty(route) {
  if (!route) return true;
  if (
    route.municipio_origem ||
    route.regiao_origem ||
    route.municipio_destino ||
    route.regiao_destino ||
    route.itinerary_id
  ) {
    return false;
  }
  if (!isTpNaoDefinido(route.tp_processo)) return false;
  if (route.tp_operacao != null) return false;
  const hasCargaData = (route.carga || []).some(
    (carga) => carga && (carga.param_tipo_carga || carga.tp_carga),
  );
  if (hasCargaData) return false;
  if (String(route.descricao || "").trim()) return false;
  return (route.vigencia || []).every(isValidityBlank);
}

/** Validação do Salvar (etapa 3) — TMS-6397. Todas as rotas precisam estar ok. */
export function isConfigRoutesValid(form) {
  return describeConfigRoutesGaps(form).length === 0;
}
